import {type DocumentNode, gql} from '@apollo/client';
import {
    type GqlDownloadRequestPayload,
    type GqlRequest,
    type IDownloadByLinkRequestBuilder,
    maxServerDownloadPageSize,
} from '@igp/shared/api';
import type {
    QueryExportAgentNegativeCarryoverReportArgs,
    QueryExportWeeklySummaryArgs,
    QueryGetAgentNegativeCarryoverReportArgs,
    QueryGetWeeklySummaryExportArgs,
} from '@igp/shared/contract/pam';

import {AgentLevelReportV2FilterBuilder} from '../agent-level-report-v2/requestBuilder';

import type {
    NegativeCarryoverReportDownloadFields,
    NegativeCarryoverReportQueryFields,
    NegativeCarryoverWeeklySummaryDownloadFields,
    NegativeCarryoverWeeklySummaryQueryFields,
} from './types';

export class NegativeCarryoverReportBuilder
    extends AgentLevelReportV2FilterBuilder<QueryGetAgentNegativeCarryoverReportArgs, NegativeCarryoverReportQueryFields>
    implements IDownloadByLinkRequestBuilder<QueryExportAgentNegativeCarryoverReportArgs, NegativeCarryoverReportDownloadFields>
{
    buildDownloadRequest(
        downloadRequestPayload: GqlDownloadRequestPayload<NegativeCarryoverReportDownloadFields>
    ): GqlRequest<QueryExportAgentNegativeCarryoverReportArgs> {
        const filter = this.mapToFilterObject(downloadRequestPayload.filter);
        const queryArgs = this.getGqlPageArgs(filter);
        return {
            query: this.buildExportQuery(),
            variables: {
                report_name: downloadRequestPayload.report_name,
                filter: queryArgs?.filter,
                sort: queryArgs?.sort,
                start: 0,
                end: maxServerDownloadPageSize,
                fields: downloadRequestPayload.columns.map(column => ({
                    column_name: column.name,
                    field: column.field,
                })),
            },
        };
    }

    protected buildQuery(fields: NegativeCarryoverReportQueryFields[]): DocumentNode {
        const requiredFields: NegativeCarryoverReportQueryFields[] = [
            'items.id',
            'items.agent_uid',
            'items.week_start_date_ts.seconds',
            'last_updated.seconds',
            'total_count',
        ];
        const queryFields = [...new Set([...fields, ...requiredFields])];
        return gql`
            query GetAgentNegativeCarryoverReport($filter: AgentRefereeRevenueReportFilter, $sort: Sorting, $start: Int, $end: Int) {
                getAgentNegativeCarryoverReport(filter: $filter, sort: $sort, end: $end, start: $start) {
                    ${this.buildQueryFields(queryFields)}
                }
            }
        `;
    }

    private buildExportQuery() {
        return gql`
            query ExportAgentNegativeCarryoverReport(
                $filter: AgentRefereeRevenueReportFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: Sorting
                $start: Int
                $end: Int
            ) {
                exportAgentNegativeCarryoverReport(
                    filter: $filter
                    report_name: $report_name
                    fields: $fields
                    sort: $sort
                    end: $end
                    start: $start
                ) {
                    url
                }
            }
        `;
    }
}

export class NegativeCarryoverWeeklySummaryRequestBuilder
    extends AgentLevelReportV2FilterBuilder<QueryGetWeeklySummaryExportArgs, NegativeCarryoverWeeklySummaryQueryFields>
    implements IDownloadByLinkRequestBuilder<QueryExportWeeklySummaryArgs, NegativeCarryoverWeeklySummaryDownloadFields>
{
    buildDownloadRequest(
        downloadRequestPayload: GqlDownloadRequestPayload<NegativeCarryoverWeeklySummaryDownloadFields>
    ): GqlRequest<QueryExportWeeklySummaryArgs> {
        const filter = this.mapToFilterObject(downloadRequestPayload.filter);
        const queryArgs = this.getGqlPageArgs(filter);
        return {
            query: this.buildExportQuery(),
            variables: {
                report_name: downloadRequestPayload.report_name,
                filter: queryArgs?.filter,
                sort: queryArgs?.sort,
                start: 0,
                end: maxServerDownloadPageSize,
                fields: downloadRequestPayload.columns.map(column => ({
                    column_name: column.name,
                    field: column.field,
                })),
            },
        };
    }

    protected buildQuery(fields: NegativeCarryoverWeeklySummaryQueryFields[]): DocumentNode {
        const requiredFields: NegativeCarryoverWeeklySummaryQueryFields[] = ['items.agent_uid', 'total_count'];
        const queryFields = [...new Set([...fields, ...requiredFields])];
        return gql`
            query GetWeeklySummaryExport($filter: AgentRefereeRevenueReportFilter, $sort: Sorting, $start: Int, $end: Int) {
                getWeeklySummaryExport(filter: $filter, sort: $sort, end: $end, start: $start) {
                    ${this.buildQueryFields(queryFields)}
                }
            }
        `;
    }

    private buildExportQuery() {
        return gql`
            query ExportWeeklySummary(
                $filter: AgentRefereeRevenueReportFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: Sorting
                $start: Int
                $end: Int
            ) {
                exportWeeklySummary(filter: $filter, report_name: $report_name, fields: $fields, sort: $sort, end: $end, start: $start) {
                    url
                }
            }
        `;
    }
}
